import { getWallPath } from "building-generation/elements/walls/wall";
import {
  sectionViewAABBOnFrontPlane,
  sectionViewFrontPlane,
} from "building-generation/generationTools/sectionViewClippingPlane";
import type { Building } from "building-types/building/buildingTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { SectionViewGeometry } from "building-types/views/View3D";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { assertIsNotNull } from "common/utils/assertOfType";
import { faceNew, faceOrNullCopy, faceOrNullMoveAlongNormal, faceOrNullReverse } from "geom/brep";
import { buildBSPNode, buildEdgePolygon, buildPolygonsFromFaces } from "geom/bsp";
import { clipFace, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON_SQUARED } from "geom/core";
import { planeNewXY } from "geom/plane";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2FromAABB2,
  polyLine2MultiCentroid,
  polyLine2SetCW,
  polyLine2SummedArea,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2DistSquared } from "geom/vec2";
import { ROOM_LABEL_FONT_SIZE } from "pdf/planFonts";
import type { Label } from "./label";
import { labelNew } from "./label";

interface RoomSectionVolume {
  readonly label: string;
  readonly shortLabel: string;
  readonly volume: NodeBSP | null;
  readonly volumePolygons: PolygonBSP[];
}

function roomSectionVolumeNew(
  label: string,
  shortLabel: string,
  volume: NodeBSP | null,
  volumePolygons: PolygonBSP[],
): RoomSectionVolume {
  return {
    label,
    shortLabel,
    volume,
    volumePolygons,
  };
}

export function buildRoomWallPolygons(room: Room, walls: Table<WallSystem>): PolygonBSP[] {
  const volumePolygons: PolygonBSP[] = [];

  const { level } = room;

  if (!level) {
    return volumePolygons;
  }

  const wallIndex = walls[level.id].wallIndex;
  const roomWalls = wallIndex.wallsInRoom(room);

  for (const wall of roomWalls) {
    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    const { wallFaces } = wall;
    const path = getWallPath(wall);
    const { leftRoom } = path;
    const { leftFaces, rightFaces } = wallFaces;

    buildPolygonsFromFaces({
      faces: leftRoom === room ? leftFaces : rightFaces,
      polygons: volumePolygons,
    });
  }

  return volumePolygons;
}

export function buildRoomVolumePolygons(room: Room): PolygonBSP[] {
  const volumePolygons: PolygonBSP[] = [];

  const { level, outline } = room;

  if (!level) {
    return volumePolygons;
  }

  const { floorElevation, topOfWall } = level;

  // add outline polygons
  for (let i = 0, l = polyLine2EdgeCount(outline); i < l; i++) {
    const componentIndex = i * 2;

    const ax = outline[componentIndex];
    const ay = outline[componentIndex + 1];
    const bx = outline[componentIndex + 2];
    const by = outline[componentIndex + 3];

    if (vec2DistSquared(ax, ay, bx, by) < DIST_EPSILON_SQUARED) {
      break;
    }

    const polygon = buildEdgePolygon(ax, ay, floorElevation, bx, by, floorElevation, 0.0, 0.0, topOfWall);
    assertIsNotNull(polygon);
    volumePolygons.push(polygon);
  }

  // add the floor and ceiling
  const topFace = faceNew([polyLine2SetCW(polyLine2Copy(outline))], planeNewXY(0, 0, floorElevation + topOfWall));
  const bottomFace = faceOrNullMoveAlongNormal(faceOrNullReverse(faceOrNullCopy(topFace)), topOfWall);

  buildPolygonsFromFaces({
    faces: [topFace, bottomFace],
    polygons: volumePolygons,
  });

  return volumePolygons;
}

function buildRoomVolume(room: Room): RoomSectionVolume {
  const { attributes, nameInPlan } = room;
  const label = nameInPlan || attributes.name;
  const shortLabel = nameInPlan ? nameInPlan.slice(0, 4) + "." : attributes.shortName;

  // room volume polygons
  const volumePolygons = buildRoomVolumePolygons(room);

  // build the BSP volume
  const volume = buildBSPNode({ polygons: volumePolygons });

  return roomSectionVolumeNew(label, shortLabel, volume, volumePolygons);
}

interface BuildSectionLabelsOptions {
  readonly sectionView: SectionViewGeometry;
  readonly building: Building;
}

/**
 * Room labels for a section's cut plane, in the section's front-plane 2D coordinates.
 *
 * Consumed by the `ROOM_LABELS` section geometry generator
 * ({@link makeSheetGeometryFromSectionRoomLabels}).
 */
export function buildOrthogonalSectionLabels({ sectionView, building }: BuildSectionLabelsOptions): Label[] {
  const sectionLabels: Label[] = [];

  const mirror = building?.mirror ?? false;
  const mirrorTranslationX = building?.mirrorTranslationX ?? 0;

  const sectionPlane = sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX);

  if (!sectionPlane) {
    return sectionLabels;
  }

  // build room volumes
  const { rooms } = building;
  const roomVolumes = Object.values(rooms).map((x) => buildRoomVolume(x));

  // build face from section planes
  const sectionFrontPL = sectionViewAABBOnFrontPlane(sectionView, mirror, mirrorTranslationX);
  const sectionFrontFace = faceNew([polyLine2SetCW(polyLine2FromAABB2(sectionFrontPL))], sectionPlane);

  // find the intersection of the section plane with each room volume
  for (const { label, shortLabel, volume } of roomVolumes) {
    if (!volume) {
      continue;
    }

    const insideFaces = clipFace({
      face: sectionFrontFace,
      options: {
        clippingNodes: [clippingNodeNew(volume, true)],
        coplanarsToFront: true,
      },
    });

    if (insideFaces.length <= 0) {
      continue;
    }

    const pls: PolyLine2[] = insideFaces.map((x) => x.loops[0]);
    const [x, y] = polyLine2MultiCentroid(pls);
    const area = polyLine2SummedArea(pls) / 144;

    const finalLabel = area < 20 ? shortLabel : label;

    sectionLabels.push(
      labelNew({
        text: finalLabel.toUpperCase().replace(" ", "\n"),
        fontSize: ROOM_LABEL_FONT_SIZE,
        centerX: x,
        centerY: y,
      }),
    );
  }

  return sectionLabels;
}
